import { useEffect, useReducer, useRef, useState, type SyntheticEvent } from "react";
import { DateInput } from "semantic-ui-calendar-react-yz";
import moment, { updateLocale, type Moment } from "moment";
import "moment/locale/en-gb";
import cn from "classnames";

import "./datePicker.scss";

export type Props = {
  onChange: (e: SyntheticEvent<HTMLElement>, data: { name: string; value: string }) => void;
  closeOnMouseLeave?: boolean;
  className?: string;
  disabled?: boolean;
  format?: string;
  highlightExpired?: boolean;
  horizontalPosition?: "auto" | "left" | "right";
  iconPosition?: "left" | "right";
  initialDate?: Moment | string;
  marked?: Moment[] | Date[];
  maxDate?: Moment | string;
  minDate?: Moment | string;
  mountNode?: React.ReactNode;
  placeholder?: string;
  popupPosition?:
    | "top left"
    | "top right"
    | "bottom left"
    | "bottom right"
    | "right center"
    | "left center"
    | "top center"
    | "bottom center";
  value?: string;
  verticalPosition?: "top" | "bottom";
};

const validDateFormats = [
  "M/D/YY",
  "MM/D/YY",
  "M/DD/YY",
  "MM/DD/YY",
  "M/D/YYYY",
  "MM/D/YYYY",
  "M/DD/YYYY",
  "MM/DD/YYYY",
];
const isValidCheck = (value: string) => moment(value, validDateFormats, true).isValid();

const DatePicker = ({
  placeholder = "",
  value = "",
  iconPosition = "left",
  closeOnMouseLeave = true,
  mountNode,
  className,
  onChange,
  verticalPosition = "bottom",
  horizontalPosition = "auto",
  minDate,
  maxDate,
  initialDate,
  marked,
  popupPosition,
  disabled,
  format,
  highlightExpired,
  ...additionalProps
}: Props) => {
  const containerRef = useRef(null);
  const [_, forceUpdate] = useReducer(x => x + 1, 0);
  const [query, setQuery] = useState(value);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  const markDate = (marked: Date[] | Moment[] | undefined, value: string) => {
    if (marked) return marked;
    if (value) return undefined; // Value already marks the date without extra work
    return [new Date()];
  };

  const updateLocaleFormats = () => {
    updateLocale("en-gb", {
      months: [
        "January,",
        "February,",
        "March,",
        "April,",
        "May,",
        "June,",
        "July,",
        "August,",
        "September,",
        "October,",
        "November,",
        "December,",
      ],
      weekdaysMin: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    });
  };

  useEffect(() => {
    updateLocaleFormats();
    forceUpdate();
  }, []);

  const handleClick = () => {
    return containerRef.current || undefined;
  };

  useEffect(() => {
    const isValid = isValidCheck(query);
    if (isValid && !focused) {
      onChange({} as SyntheticEvent<HTMLInputElement>, { name: "date", value: query });
    } else if (!isValid && !focused) {
      onChange({} as SyntheticEvent<HTMLInputElement>, { name: "date", value: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, focused]);

  useEffect(() => {
    const isValid = isValidCheck(query);
    if (!isValid && !focused && query !== "") {
      setQuery("");
    }
  }, [query, focused]);

  return (
    <div className={cn("date-picker", className)} data-testid="date-picker">
      <div
        className={cn("date-picker-container", verticalPosition, horizontalPosition)}
        ref={containerRef}
        data-testid="date-picker-container"
      />
      <DateInput
        autoComplete="off"
        marked={markDate(marked, value)}
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
        initialDate={initialDate}
        localization="en"
        dateFormat={format ?? "MM/DD/YYYY"}
        className={cn("input", { expired: highlightExpired })}
        closable
        name="date"
        disabled={disabled}
        placeholder={placeholder}
        value={query}
        minDate={minDate} // may not work if Value month !== minDate month (error: a white page) but if initialDate is set it works
        maxDate={maxDate}
        iconPosition={iconPosition}
        closeOnMouseLeave={closeOnMouseLeave}
        mountNode={mountNode || handleClick()}
        onChange={(_, data) => setQuery(data.value)}
        popupPosition={popupPosition ?? "bottom left"}
        {...additionalProps}
      />
    </div>
  );
};

export default DatePicker;
